/**
 * UserDashboard - Task history, stats, and activity
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  BarChart3, Zap, Image, Video, MessageSquare, Clock, 
  Gift, Users, CreditCard, ArrowRight, Loader2,
  CheckCircle, XCircle, Clock4, Copy, Share2, Download
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserDashboard() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [taskHistory, setTaskHistory] = useState([]);
  const [mediaHistory, setMediaHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize
    if (!user || !token) {
      navigate('/auth?redirect=/user-dashboard');
      return;
    }
    fetchDashboard();
  }, [user, token, authLoading]);

  const fetchDashboard = async () => {
    try {
      const [dashRes, historyRes, mediaRes] = await Promise.all([
        axios.get(`${API}/user/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/user/task-history?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/user/media-history?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setDashboard(dashRes.data);
      setTaskHistory(historyRes.data.tasks);
      setMediaHistory(mediaRes.data.media || []);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/auth?mode=register&ref=${dashboard?.referral?.code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const referral = dashboard?.referral || {};

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome back, {dashboard?.user?.name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-zinc-400">
            Here's what's happening with your AI tasks
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-4"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-teal-400" />
              </div>
              <span className="text-zinc-400 text-sm">Tasks Completed</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.completed_tasks || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-4"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                <Image className="w-5 h-5 text-pink-400" />
              </div>
              <span className="text-zinc-400 text-sm">Media Generated</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.media_generated || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-4"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-zinc-400 text-sm">Chatbots</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.chatbots_created || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-4"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-zinc-400 text-sm">Free Tasks Left</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {stats.free_tasks_remaining || 0}
              <span className="text-zinc-500 text-sm font-normal">/{stats.free_tasks_limit || 5}</span>
            </p>
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Task History */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-zinc-900/50 border border-white/10 rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Recent Tasks</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/my-orders')}
                  className="text-teal-400"
                >
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {taskHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Zap className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400 mb-4">No tasks yet</p>
                  <Button onClick={() => navigate('/templates')} className="bg-teal-500 hover:bg-teal-600">
                    Start Your First Task
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {taskHistory.map((task, index) => (
                    <div
                      key={task.id || index}
                      className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          task.status === 'completed' ? 'bg-green-500/20' :
                          task.status === 'failed' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                        }`}>
                          {task.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : task.status === 'failed' ? (
                            <XCircle className="w-4 h-4 text-red-400" />
                          ) : (
                            <Clock4 className="w-4 h-4 text-yellow-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">
                            {task.template_name || 'Task'}
                          </p>
                          <p className="text-zinc-500 text-xs">
                            {new Date(task.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {task.is_free_task ? (
                          <span className="text-green-400 text-sm">Free</span>
                        ) : (
                          <span className="text-zinc-400 text-sm">
                            ${((task.amount || 0) / 100).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Media Gallery */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 mt-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">My Media</h2>
                <span className="text-zinc-400 text-sm">{mediaHistory.length} items</span>
              </div>

              {mediaHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Image className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400">No media generated yet</p>
                  <Button
                    onClick={() => navigate('/templates')}
                    className="mt-4 bg-teal-500 hover:bg-teal-600"
                  >
                    Generate Your First Image
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {mediaHistory.map((media) => (
                    <div 
                      key={media.id}
                      className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700 group relative hover:border-teal-500/50 transition-colors cursor-pointer"
                    >
                      {media.type === 'image' ? (
                        <div className="aspect-square bg-zinc-900 relative overflow-hidden">
                          {media.image_data ? (
                            <img 
                              src={`data:image/png;base64,${media.image_data}`}
                              alt={media.prompt?.substring(0, 30)}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-8 h-8 text-zinc-600" />
                            </div>
                          )}
                          {/* Download overlay on hover */}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = document.createElement('a');
                                link.href = `data:image/png;base64,${media.image_data}`;
                                link.download = `tripletc-${media.id}.png`;
                                link.click();
                              }}
                              className="bg-teal-500 hover:bg-teal-600"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="aspect-video bg-zinc-900 relative overflow-hidden">
                          {media.thumbnail ? (
                            <>
                              <img 
                                src={`data:image/jpeg;base64,${media.thumbnail}`}
                                alt={media.prompt?.substring(0, 30)}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center">
                                  <Video className="w-6 h-6 text-white" />
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Video className="w-8 h-8 text-zinc-600" />
                            </div>
                          )}
                          {/* Download overlay on hover */}
                          {media.video_data && (
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const link = document.createElement('a');
                                  link.href = `data:video/mp4;base64,${media.video_data}`;
                                  link.download = `tripletc-${media.id}.mp4`;
                                  link.click();
                                }}
                                className="bg-teal-500 hover:bg-teal-600"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="p-2">
                        <p className="text-zinc-300 text-xs truncate">{media.prompt?.substring(0, 30)}...</p>
                        <p className="text-zinc-500 text-xs mt-1">
                          {media.type === 'video' ? `${media.duration || 4}s video` : 'Image'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Column - Referral & Quick Actions */}
          <div className="space-y-6">
            {/* Referral Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="bg-gradient-to-br from-teal-500/10 to-blue-500/10 border border-teal-500/20 rounded-xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Refer a Friend</h3>
                  <p className="text-zinc-400 text-sm">Share TripleTC with others</p>
                </div>
              </div>

              <div className="bg-zinc-900/50 rounded-lg p-3 mb-4">
                <p className="text-zinc-400 text-xs mb-1">Your referral code</p>
                <div className="flex items-center justify-between">
                  <span className="text-white font-mono text-lg">{referral.code || 'N/A'}</span>
                  <button
                    onClick={copyReferralLink}
                    className="text-teal-400 hover:text-teal-300"
                  >
                    {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-between text-sm mb-4">
                <span className="text-zinc-400">Referrals</span>
                <span className="text-white font-semibold">{referral.total_referrals || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Credits Earned</span>
                <span className="text-teal-400 font-semibold">${referral.credits_earned?.toFixed(2) || '0.00'}</span>
              </div>

              <Button
                onClick={copyReferralLink}
                className="w-full mt-4 bg-teal-500 hover:bg-teal-600"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {copied ? 'Link Copied!' : 'Share Referral Link'}
              </Button>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-zinc-900/50 border border-white/10 rounded-xl p-6"
            >
              <h3 className="text-white font-semibold mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-zinc-300 hover:text-white hover:bg-zinc-800"
                  onClick={() => navigate('/templates')}
                >
                  <Zap className="w-4 h-4 mr-2 text-teal-400" />
                  New Task
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-zinc-300 hover:text-white hover:bg-zinc-800"
                  onClick={() => navigate('/chatbot-builder')}
                >
                  <MessageSquare className="w-4 h-4 mr-2 text-purple-400" />
                  Create Chatbot
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-zinc-300 hover:text-white hover:bg-zinc-800"
                  onClick={() => navigate('/my-chatbots')}
                >
                  <BarChart3 className="w-4 h-4 mr-2 text-blue-400" />
                  My Chatbots
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
